import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { prescriptionAPI } from "@/lib/prescription-api";
import {
  CreateOutsideReferralRequest,
  UpdateOutsideReferralRequest,
} from "@/types/prescription";

export const useOutsideReferral = (
  appointmentId: string,
  parentType: "prescription" | "meal_plan" = "prescription"
) => {
  const queryClient = useQueryClient();
  // Include parentType in queryKey to separate cache for doctor vs nutritionist
  const queryKey = ["outside-referral", appointmentId, parentType];

  const query = useQuery({
    queryKey,
    queryFn: () =>
      prescriptionAPI.getOutsideReferral(appointmentId, parentType),
    retry: false,
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateOutsideReferralRequest) =>
      prescriptionAPI.createOutsideReferral(appointmentId, data),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKey, data);
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: UpdateOutsideReferralRequest;
    }) => prescriptionAPI.updateOutsideReferral(appointmentId, id, data),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKey, data);
      queryClient.invalidateQueries({ queryKey });
    },
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    create: createMutation.mutate,
    update: updateMutation.mutate,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
  };
};
